import {
  Component,
  OnInit,
  ViewEncapsulation,
  WritableSignal,
  computed,
  inject,
  signal
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  MovementBatchResponse,
  MovementControllerService,
  MovementItemRequest,
  MovementProductValidationDTO,
  MovementSendRequest,
  MovementTypeDTO
} from 'src/app/admin-api';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ItemGroup = FormGroup<{
  keyType: FormControl<string>;
  code: FormControl<string>;
  quantity: FormControl<number>;
}>;

@Component({
  selector: 'app-movements',
  templateUrl: './movements.component.html',
  styleUrl: './movements.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [ConfirmationService]
})
export class MovementsComponent implements OnInit {
  private movementApi = inject(MovementControllerService);
  private permissionsService = inject(NgxPermissionsService);
  private confirmationService = inject(ConfirmationService);
  private title = inject(Title);
  private router = inject(Router);

  keyTypeOptions = [
    { label: 'EAN', value: 'EAN' },
    { label: 'SKU', value: 'SKU' }
  ];
  readonly fullWidthStyle = { width: '100%' };

  // Estado reativo (signals) que influencia o template.
  permission: WritableSignal<boolean | undefined> = signal(undefined);
  types: WritableSignal<MovementTypeDTO[]> = signal([]);
  typeOptions: WritableSignal<{ label: string; value: string }[]> = signal([]);
  movementType = signal('');
  selectedTypeLabel = signal('');
  stockBucketLabel = signal('estoque');
  hasMultipleItems = signal(false);

  validEmails: WritableSignal<string[]> = signal([]);
  invalidEmails: WritableSignal<string[]> = signal([]);

  // Validação por linha de produto (paralelos ao FormArray items).
  validations: WritableSignal<(MovementProductValidationDTO | null)[]> = signal([]);
  availableStocks: WritableSignal<(number | null)[]> = signal([]);

  // Resultado do dry-run. Qualquer edição invalida a pré-validação.
  preview: WritableSignal<MovementBatchResponse | null> = signal(null);

  eligibleEmails = computed(() =>
    (this.preview()?.results ?? [])
      .filter((r) => r.status === 'ELIGIBLE')
      .map((r) => r.email ?? '')
  );
  previewNotFound = computed(() =>
    (this.preview()?.results ?? [])
      .filter((r) => r.status === 'NOT_FOUND')
      .map((r) => r.email ?? '')
  );

  private itemsValid = computed(() => {
    const validations = this.validations();
    return validations.length > 0 && validations.every((v) => !!v && v.valid);
  });

  canValidate = computed(
    () =>
      this.permission() === true &&
      !!this.movementType() &&
      this.itemsValid() &&
      this.validEmails().length > 0
  );

  canSubmit = computed(
    () =>
      this.canValidate() &&
      this.preview()?.stockValid === true &&
      this.eligibleEmails().length > 0
  );

  sendLabel = computed(() =>
    this.eligibleEmails().length
      ? `Gerar movimentação (${this.eligibleEmails().length})`
      : 'Gerar movimentação'
  );

  form = new FormGroup({
    movementType: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    items: new FormArray<ItemGroup>([]),
    emails: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    })
  });

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    this.title.setTitle('Marketing - Gerador de Movimentações');
    try {
      this.permission.set(
        await this.permissionsService.hasPermission([
          roleAsObject(Role.Full_Administrator).enumValue,
          roleAsObject(Role.Marketing).enumValue,
          roleAsObject(Role.Logistics).enumValue
        ])
      );
      const types = await lastValueFrom(
        this.movementApi.listTypes().pipe(map((r) => r.result ?? []))
      );
      this.types.set(types);
      this.typeOptions.set(
        types.map((t) => ({ label: t.label ?? t.code ?? '', value: t.code ?? '' }))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
    this.addItem();
    if (!this.permission()) {
      this.form.disable();
    }
  }

  get itemsArray(): FormArray<ItemGroup> {
    return this.form.controls.items;
  }

  goToHistory(): void {
    this.router.navigate(['/marketing/movements/history']);
  }

  invalidControl(control: 'movementType' | 'emails'): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.dirty || field.touched);
  }

  invalidItemCode(index: number): boolean {
    const field = this.itemsArray.at(index).controls.code;
    return field.invalid && (field.dirty || field.touched);
  }

  private selectedType(): MovementTypeDTO | undefined {
    return this.types().find((t) => t.code === this.movementType());
  }

  async onTypeChange(): Promise<void> {
    this.movementType.set(this.form.controls.movementType.value);
    const type = this.selectedType();
    this.selectedTypeLabel.set(type?.label ?? '');
    this.stockBucketLabel.set(this.bucketLabel(type?.stockBucket));
    // O saldo disponível depende do bucket do tipo — revalida as linhas preenchidas.
    await this.revalidateItems();
    this.clearPreview();
  }

  // ------- Produtos -------

  addItem(): void {
    this.itemsArray.push(
      new FormGroup({
        keyType: new FormControl<string>('EAN', { nonNullable: true }),
        code: new FormControl<string>('', {
          nonNullable: true,
          validators: [Validators.required]
        }),
        quantity: new FormControl<number>(1, {
          nonNullable: true,
          validators: [Validators.required, Validators.min(1)]
        })
      })
    );
    this.validations.set([...this.validations(), null]);
    this.availableStocks.set([...this.availableStocks(), null]);
    this.hasMultipleItems.set(this.itemsArray.length > 1);
    this.clearPreview();
  }

  removeItem(index: number): void {
    this.itemsArray.removeAt(index);
    this.validations.set(this.validations().filter((_, i) => i !== index));
    this.availableStocks.set(this.availableStocks().filter((_, i) => i !== index));
    this.hasMultipleItems.set(this.itemsArray.length > 1);
    this.clearPreview();
  }

  /** Valida um produto por EAN/SKU ao adicionar/editar a linha. */
  async validateItem(index: number): Promise<void> {
    const group = this.itemsArray.at(index);
    const code = (group.controls.code.value || '').trim();
    if (!code) {
      this.setValidation(index, null, null);
      this.clearPreview();
      return;
    }
    LoaderService.showLoader();
    try {
      const dto = await lastValueFrom(
        this.movementApi
          .validateProduct({
            code,
            keyType: group.controls.keyType.value,
            movementType: this.movementType() || undefined
          })
          .pipe(map((r) => r.result))
      );
      // Não permite o mesmo produto (variante) em mais de uma linha.
      const alreadyAdded =
        dto?.productVariantId != null &&
        this.validations().some(
          (v, i) => i !== index && v?.productVariantId === dto.productVariantId
        );
      if (alreadyAdded) {
        group.controls.code.setValue('');
        this.setValidation(index, null, null);
        AppDialogService.showInfoDialog({
          header: 'Produto já adicionado',
          message: 'Este produto já está no lote. Cada produto pode ser adicionado uma única vez.'
        });
        return;
      }
      this.setValidation(index, dto ?? null, this.computeAvailable(dto ?? null));
    } catch (error) {
      this.setValidation(index, null, null);
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
      this.clearPreview();
    }
  }

  private setValidation(
    index: number,
    dto: MovementProductValidationDTO | null,
    stock: number | null
  ): void {
    const validations = [...this.validations()];
    const stocks = [...this.availableStocks()];
    validations[index] = dto;
    stocks[index] = stock;
    this.validations.set(validations);
    this.availableStocks.set(stocks);
  }

  private async revalidateItems(): Promise<void> {
    for (let i = 0; i < this.itemsArray.length; i++) {
      if ((this.itemsArray.at(i).controls.code.value || '').trim()) {
        await this.validateItem(i);
      }
    }
  }

  /** Saldo do bucket do tipo selecionado, a partir dos 4 saldos retornados na validação. */
  private computeAvailable(dto: MovementProductValidationDTO | null): number | null {
    if (!dto) return null;
    switch (this.selectedType()?.stockBucket) {
      case 'FIDELIZATION':
        return dto.inStockFidelization ?? 0;
      case 'MARKETING':
        return dto.inStockReserveMarketing ?? 0;
      case 'INFLUENCER_B4A':
        return dto.inStockInfluencerB4A ?? 0;
      default:
        return null;
    }
  }

  private bucketLabel(bucket?: string): string {
    switch (bucket) {
      case 'FIDELIZATION':
        return 'Fidelização';
      case 'MARKETING':
        return 'Reserva Marketing';
      case 'INFLUENCER_B4A':
        return 'Influencer B4A';
      default:
        return 'estoque';
    }
  }

  // ------- E-mails -------

  parseEmails(): void {
    const raw = this.form.controls.emails.value || '';
    const tokens = raw
      .split(/[,;\n\r\t ]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    const seen = new Set<string>();
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const token of tokens) {
      const key = token.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      if (EMAIL_REGEX.test(token)) valid.push(token);
      else invalid.push(token);
    }
    this.validEmails.set(valid);
    this.invalidEmails.set(invalid);
    this.clearPreview();
  }

  // ------- Preview / envio -------

  clearPreview(): void {
    this.preview.set(null);
  }

  private buildRequest(): MovementSendRequest {
    const items: MovementItemRequest[] = this.itemsArray.controls.map((group, index) => {
      const validation = this.validations()[index];
      const quantity = group.controls.quantity.value;
      if (validation?.productVariantId) {
        return { productVariantId: validation.productVariantId, quantity };
      }
      const code = (group.controls.code.value || '').trim();
      return group.controls.keyType.value === 'SKU'
        ? { sku: code, quantity }
        : { ean: code, quantity };
    });
    // shippingCompanyInfo é resolvido no backend pelo default do tipo.
    return {
      movementType: this.form.controls.movementType.value,
      emails: this.validEmails(),
      items
    };
  }

  /** Dry-run: valida e-mails (conta Glambox) e estoque do lote, sem criar movimentações. */
  async validate(): Promise<void> {
    if (!this.canValidate()) return;
    LoaderService.showLoader();
    try {
      const preview = await lastValueFrom(
        this.movementApi.preview(this.buildRequest()).pipe(map((r) => r.result))
      );
      this.preview.set(preview ?? null);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  confirmSend(): void {
    if (!this.canSubmit()) return;
    const skipped = this.previewNotFound().length;
    const skippedNote =
      skipped > 0 ? ` ${skipped} e-mail(s) não encontrado(s) ficarão de fora.` : '';
    this.confirmationService.confirm({
      header: 'Confirmar geração de movimentação',
      message:
        `Gerar "${this.selectedTypeLabel()}" para ${this.eligibleEmails().length} e-mail(s) ` +
        `com ${this.itemsArray.length} produto(s)?${skippedNote}`,
      acceptLabel: 'Gerar',
      rejectLabel: 'Cancelar',
      accept: () => this.submit()
    });
  }

  private async submit(): Promise<void> {
    if (!this.canSubmit()) return;
    LoaderService.showLoader();
    try {
      const result = await lastValueFrom(
        this.movementApi.send(this.buildRequest()).pipe(map((r) => r.result))
      );
      AppDialogService.showInfoDialog({
        header: 'Concluído',
        message:
          `${result?.succeeded ?? 0} enviada(s) com sucesso, ${result?.failed ?? 0} falha(s), ` +
          `${result?.notFound ?? 0} não encontrado(s).`
      });
      await this.router.navigate(['/marketing/movements/history']);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }
}
